import { t } from '@lingui/macro';
import {
    isDate,
    parse,
    parseISO,
    isValid,
    differenceInCalendarDays,
    differenceInMinutes,
    differenceInDays,
    differenceInHours,
    differenceInCalendarWeeks,
    isToday,
    isYesterday,
    lastDayOfWeek,
    isFirstDayOfMonth,
    isLastDayOfMonth,
    startOfYear,
    endOfYear,
} from 'date-fns';

export const FORMAT_DATE = 'dd.MM.yy';
export const FORMAT_DATE_ISO = 'yyyy-MM-dd';
export const FORMAT_DATE_TIME = 'dd.MM.yy, HH:mm';
export const FORMAT_DATE_TIME_FULL = 'dd.MM.yyyy, HH:mm';

const weekStartsOnMondays = { weekStartsOn: 1 as const };

export const tryParse = (value: Date | string): Date => {
    if (isDate(value)) {
        return value;
    }

    const parsedDate = parseISO(value);

    if (!isValid(parsedDate)) {
        return parse(value, FORMAT_DATE_ISO, new Date());
    }

    return parsedDate;
};

export const format = (date: Date | string | undefined, mask = 'dd.mm.yy'): string | undefined => {
    if (!date) {
        return undefined;
    }

    const value = tryParse(date);

    if (!isValid(value)) {
        return undefined;
    }

    const pad = v => (String(v).length === 1 ? `0${v}` : v);

    return mask
        .replace('dd', pad(value.getDate()))
        .replace('mm', pad(value.getMonth() + 1))
        .replace('yyyy', String(value.getFullYear()))
        .replace('yy', String(value.getFullYear()).slice(2));
};

const formatToddmmyyyy = (date: Date | undefined, mask = 'dd.mm.yyyy'): string | undefined => {
    if (!date) {
        return undefined;
    }

    const value = tryParse(date);

    if (!isValid(value)) {
        return undefined;
    }

    const pad = v => (String(v).length === 1 ? `0${v}` : v);

    return mask
        .replace('dd', pad(value.getDate()))
        .replace('mm', pad(value.getMonth() + 1))
        .replace('yyyy', String(value.getFullYear()));
};

export function formatISODate(date: Date): string | undefined {
    return format(date, 'yyyy-mm-dd');
}

export const parseFromISOString = (date: string | undefined): Date | undefined => {
    if (date === undefined) {
        return undefined;
    }
    const tempDate = new Date(date);
    return isValid(tempDate) ? parseFromCustomString(format(new Date(date))) : undefined;
};

const parseFromCustomString = (date = '', mask = 'dd.mm.yy'): Date | undefined => {
    if (!date?.length) {
        return undefined;
    }

    const sanitizedMask = mask.split(/[^dmy]/);
    const dd = sanitizedMask.findIndex(x => x === 'dd');
    const mm = sanitizedMask.findIndex(x => x === 'mm');
    const yy = sanitizedMask.findIndex(x => x === 'yy' || x === 'yyyy');

    let day;
    let month;
    let year;

    const getFullYear = y => {
        if (y && y.length === 2) {
            const z = Number.parseInt(y, 10);

            return z > 50 ? `19${y}` : `20${y}`;
        }

        return y;
    };

    const inputParts = date.split(/\D/);

    if (inputParts.length === 1) {
        const fullDate = inputParts[0];
        const unseparatedMask = mask.replace(/[^dmy]/g, '');

        day = fullDate.slice(unseparatedMask.indexOf('d'), unseparatedMask.lastIndexOf('d') + 1);
        month = fullDate.slice(unseparatedMask.indexOf('m'), unseparatedMask.lastIndexOf('m') + 1);
        year = getFullYear(fullDate.slice(unseparatedMask.indexOf('y'), unseparatedMask.lastIndexOf('y') + 3)); // account for full years
    } else {
        day = inputParts[dd];
        month = inputParts[mm];
        year = getFullYear(inputParts[yy]);
    }

    day = Number.parseInt(day, 10);
    month = Number.parseInt(month, 10);
    year = Number.parseInt(year, 10);

    const currentDate = new Date();

    return new Date(
        /* year */ Object.is(year, NaN) ? currentDate.getFullYear() : year,
        /* month */ Object.is(month, NaN) ? currentDate.getMonth() : month - 1, // months are zero based in javascript, so subtract a day
        /* day */ Object.is(day, NaN) ? currentDate.getDate() : day,
        /* hours */ 12,
        /* minutes */ 0 // set to midday to avoid UTC offset causing dates to be mismatched server side
    );
};

const differenceInCalendarDaysToSunday = date => differenceInCalendarDays(lastDayOfWeek(date, weekStartsOnMondays), date);

const differenceInCalendarDaysToMonday = date => differenceInCalendarDays(lastDayOfWeek(date, weekStartsOnMondays), date) + 1;

interface GetTimeAgoOptions {
    showMinutesAgo?: boolean;
}

export const getTimeAgo = (createdAt: Date | string, options: GetTimeAgoOptions = {}) => {
    const now = new Date();
    if (isToday(createdAt)) {
        const mins = differenceInMinutes(now, createdAt);

        if (mins <= 5) {
            return t`Just now`;
        }

        if (mins < 60) {
            return options.showMinutesAgo ? t`${mins} minutes ago` : t`Within the last hour`;
        }

        const hours = differenceInHours(now, createdAt);

        if (hours <= 5) {
            return hours === 1 ? t`1 hour ago` : t`${hours} hours ago`;
        }

        return t`Today`;
    }
    if (isYesterday(createdAt)) {
        return t`Yesterday`;
    }
    if (differenceInDays(now, createdAt) < 7) {
        const days = differenceInDays(now, createdAt);
        return t`${days} days ago`;
    }
    const weeks = differenceInCalendarWeeks(now, createdAt, weekStartsOnMondays);
    return weeks === 1 ? t`1 week ago` : t`${weeks} weeks ago`;
};

/**
 * Given two dates, returns true if the period between them is longer than a year.
 * As the number of days in an year can vary (365 or 366), this function
 * considers the dateLeft' year as the benchmark
 */
export const isPeriodEqualToOrLongerThanAYear = (dateLeft: Date, dateRight: Date): boolean => {
    return differenceInCalendarDays(dateRight, dateLeft) > differenceInCalendarDays(endOfYear(dateRight), startOfYear(dateRight));
};

/**
 * Given two dates, it returns true if the period between them are full months.
 * For example: 1st of February and 31th of August
 */
export const isPeriodCoveringFullMonths = (dateLeft: Date, dateRight: Date): boolean => {
    return isFirstDayOfMonth(dateLeft) && isLastDayOfMonth(dateRight);
};

/**
 * Normalizes a Date object to retain only the date part (year, month, day)
 */
export const normalizeToDate = (date: Date): Date => {
    // Ensuring that it is a valid instance of Date
    const currentDate = new Date(date);

    return new Date(currentDate.toISOString()?.split('T')[0]);
};

export default {
    format,
    formatToddmmyyyy,
    parseFromCustomString,
    parseFromISOString,
    differenceInCalendarDaysToSunday,
    differenceInCalendarDaysToMonday,
};
